import { useEffect, useRef } from 'react';
import type { ChangeEvent, KeyboardEvent, VoidFunctionComponent } from 'react';

import styles from './AlphanumericCodeInput.module.less';

type AlphanumericCodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  /** Length of the code (default: 12) */
  length?: number;
  /** Size of each group for formatting (default: 4) */
  groupSize?: number;
  disabled?: boolean;
};

/**
 * Input component for entering alphanumeric codes.
 * Auto-formats with hyphens (e.g., XXXX-XXXX-XXXX for length=12, groupSize=4).
 */
const AlphanumericCodeInput: VoidFunctionComponent<AlphanumericCodeInputProps> = ({
  value,
  onChange,
  onComplete,
  length = 12,
  groupSize = 4,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Format the value for display (XXXX-XXXX-XXXX)
  const formatValue = (val: string): string => {
    // Remove all non-alphanumeric characters
    const clean = val.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    // Add hyphens after every groupSize characters
    const parts: string[] = [];
    for (let i = 0; i < clean.length; i += groupSize) {
      parts.push(clean.slice(i, i + groupSize));
    }

    return parts.join('-');
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // Remove all non-alphanumeric characters
    const clean = input.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, length);

    onChange(clean);

    // Call onComplete if all characters are filled
    if (clean.length === length && onComplete) {
      setTimeout(() => {
        onComplete(clean);
      }, 100);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Allow Enter to submit if code is complete
    if (event.key === 'Enter' && value.length === length && onComplete) {
      event.preventDefault();
      onComplete(value);
    }
  };

  const displayValue = formatValue(value);

  // Calculate placeholder based on length and groupSize
  const placeholder = Array(Math.ceil(length / groupSize))
    .fill('X'.repeat(groupSize))
    .join('-')
    .slice(0, length + Math.floor((length - 1) / groupSize));

  return (
    <div className={styles.container}>
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={length + Math.floor((length - 1) / groupSize)} // chars + hyphens
        className={styles.input}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        disabled={disabled}
      />
    </div>
  );
};

export default AlphanumericCodeInput;
